import React, { useState, useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ArrowUpDown } from "lucide-react";

import { PartnerCard } from "@/modules/hub/features/PartnerNetwork/components/PartnerCard/PartnerCard";
import { FilterSidebar } from "@/modules/hub/features/PartnerNetwork/components/FilterSidebar/FilterSidebar";
import { FeaturedResources } from "@/modules/hub/features/ResourceLibrary/components/FeaturedResources/FeaturedResources";
import ActiveFiltersHandler from "../ActiveFiltersHandler/ActiveFiltersHandler";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { usePartnerNetwork } from "../../hooks/usePartnerNetwork";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileFilterToggleButton from "@/modules/hub/features/ResourceLibrary/components/MobileFilterToggleButton/MobileFilterToggleButton";
import { PartnerNetworkSkeleton } from "./PartnerNetworkSkeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trackPartnerNetworkPageViewed } from "../../analytics/partnerNetworkAnalytics";

export function PartnerNetwork() {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPartnerNetworkPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);
  const {
    form,
    resources,
    hasMore,
    totalCount,
    loading,
    isInitialLoading,
    sidebarMetadata,
    isMetadataLoading,
    fetchMore,
    handleClearFilters,
    setFeaturedOnly,
    sortOption,
    setSortOption,
    handleFavoriteToggle,
  } = usePartnerNetwork();

  const isMobile = useIsMobile();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "mostViewed", label: "Most Viewed" },
    { value: "alphabetical", label: "Title A-Z" },
  ];

  const handleViewAllFeatured = () => {
    setFeaturedOnly();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isInitialLoading || (loading && resources.length === 0)) {
    return <PartnerNetworkSkeleton />;
  }

  return (
    <MainPageWrapper
      actions={
        <MobileFilterToggleButton
          onClick={() => setIsFilterSidebarOpen(true)}
        />
      }
      subtitle="Browse resources and materials from our partner network"
      title="Partner Network"
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className={isMobile ? undefined : "w-64 shrink-0"}>
          <FilterSidebar
            filters={sidebarMetadata}
            form={form}
            isLoading={isMetadataLoading}
            isOpen={isFilterSidebarOpen}
            onOpenChange={setIsFilterSidebarOpen}
          />
        </div>

        <div className="flex-1 min-w-0">
          {/* Featured Resources Section - Only show when no active filters */}
          {!form.watch("isFavoriteOnly") &&
            !form.watch("isFeaturedOnly") &&
            (form.watch("trades") || []).length === 0 &&
            (form.watch("categories") || []).length === 0 &&
            (form.watch("businessRoles") || []).length === 0 && (
              <FeaturedResources
                context="partner-network"
                onViewAllFeatured={handleViewAllFeatured}
              />
            )}

          <ActiveFiltersHandler form={form} onClearAll={handleClearFilters} />

          {/* Sort Controls */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {totalCount > 0 && `${totalCount} partners found`}
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select
                onValueChange={(value) => setSortOption(value as any)}
                value={sortOption}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <InfiniteScroll
            dataLength={resources.length}
            endMessage={
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  No more partners to load
                </p>
              </div>
            }
            hasMore={hasMore}
            loader={
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  Loading more...
                </p>
              </div>
            }
            next={fetchMore}
            scrollableTarget="main-scroll-area"
            style={{ overflow: "visible" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {resources.length > 0 ? (
                resources.map((resource) => (
                  <div className="fade-in" key={resource.id}>
                    <PartnerCard
                      onToggleFavorite={handleFavoriteToggle}
                      resource={resource}
                    />
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400 text-lg">
                    No partners found matching your criteria.
                  </p>
                  <button
                    className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                    onClick={handleClearFilters}
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </InfiniteScroll>
        </div>
      </div>
    </MainPageWrapper>
  );
}
